import { useCallback, useEffect, useRef } from "react";
import { useAuth } from "./useAuth";

const DEFAULT_INACTIVITY_MS = 60 * 60 * 1000;
const ACTIVITY_THROTTLE_MS = 2000;

/**
 * @param {object} [props]
 * @param {number} [props.timeoutMs] - Defaults to 1 hour.
 */
export function useInactivityLogout(props = {}) {
  const timeoutMs = props.timeoutMs ?? DEFAULT_INACTIVITY_MS;
  const { logout, user, staffProfile } = useAuth();
  const timerRef = useRef(null);
  const lastThrottleRef = useRef(0);

  const clearLogoutTimer = useCallback(() => {
    if (timerRef.current !== null) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const scheduleLogout = useCallback(() => {
    clearLogoutTimer();
    timerRef.current = setTimeout(() => {
      timerRef.current = null;
      void logout();
    }, timeoutMs);
  }, [clearLogoutTimer, logout, timeoutMs]);

  useEffect(() => {
    if (!user || !staffProfile) {
      clearLogoutTimer();
      return undefined;
    }

    const onActivity = () => {
      const now = Date.now();
      if (now - lastThrottleRef.current < ACTIVITY_THROTTLE_MS) {
        return;
      }
      lastThrottleRef.current = now;
      scheduleLogout();
    };

    const events = ["mousedown", "keydown", "scroll", "touchstart", "click"];
    events.forEach((eventName) => {
      window.addEventListener(eventName, onActivity, { passive: true });
    });
    scheduleLogout();

    return () => {
      events.forEach((eventName) => {
        window.removeEventListener(eventName, onActivity);
      });
      clearLogoutTimer();
    };
  }, [user, staffProfile, scheduleLogout, clearLogoutTimer]);
}
